import { Localized, useLocalization } from '@fluent/react'
import Button from '@mui/material/Button'
import Fab from '@mui/material/Fab'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Box, ButtonGroup, Checkbox, Dialog, Paper, TableContainer, Grid, IconButton, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { loadSysMailFooters, selectAllSysMailFooters, selectSysMailFooters } from './sysMailFootersSlice'
import { SysMailFooterDialog } from './SysMailFooterDialog'
import { showSuccess } from '../../../notifications/notificationsSlice'
import { AppId } from '../../../../app/appTypes'
import { selectModule } from '../../../modules/moduleSlice'
import { If } from '../../../../app/If'
import { LoadingContainer } from '../../../../app/LoadingContainer'
import { ErrorContainer } from '../../../../app/ErrorContainer'
import { selectSettings } from '../../../settings/settingsSlice'
import { enUS, plPL } from '@mui/x-data-grid/locales'
import { SysMailFootersGenerateCodeDialog } from './SysMailFootersGenerateCodeDialog'
import { QuickSearchToolbar } from '../../../../app/QuickSearchToolbar'

type DialogState =
    | { type: "none" }
    | { type: "updateFooter", templateId: AppId | undefined, editable: boolean }
    | { type: "newFooter", editable: boolean }
    | { type: "generateCode" }

   


const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
    const dispatch = useAppDispatch()
    const onSuccess = useCallback(() => {
        setter({ type: "none" })
        dispatch(showSuccess("saved"))
    }, [setter, dispatch])
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null

        case "updateFooter":
            return <SysMailFooterDialog
                id={state.templateId}
                editable={state.editable}
                onSaveSuccess={onSuccess}
                onCancel={onCancel}
            />

        case "newFooter":
            return <SysMailFooterDialog
            id={undefined}
            editable={state.editable}
            onSaveSuccess={onSuccess}
            onCancel={onCancel}
            />
        
        case "generateCode":
            return <SysMailFootersGenerateCodeDialog
                onCancel={onCancel}
            />
    }
}

export const SysMailFooters = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const { state: sysMailFootersState } = useAppSelector(selectSysMailFooters)
    const rows = useAppSelector(state => selectAllSysMailFooters(state))
    const currentModule = useAppSelector(state => selectModule(state).currentModule)
    const isSuper = currentModule?.code === "super"

    const [dialog, setDialog] = useState<DialogState>({ type: "none" })
    const [selectedRow, setSelectedRow] = useState<any>(null)
    const handleNewTemplate = useCallback(() => {
        setDialog({
            type: "newFooter",
            editable: isSuper
        })
    }, [setDialog, isSuper])

    const handleEditTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "updateFooter",
            templateId,
            editable: isSuper
        })
    }, [setDialog, isSuper])

    const handleGenerateCode = useCallback(() => {
        setDialog({
            type: "generateCode"
        })
    }, [setDialog])


    useEffect(() => {
        if (sysMailFootersState.type === "empty") {
            dispatch(loadSysMailFooters())
        }
    }, [dispatch, sysMailFootersState])
    
    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };


    const columns: GridColDef[] =
        [
            {
                field: 'name',
                headerName: l10n.getString("sys-mail-footers-name", null, "_Nazwa"),
                flex: 1
            },
            {
                field: 'isDefault',
                headerName: l10n.getString("sys-mail-footers-is-default", null, "_Domyślne"),
                flex: 1,
                renderCell: (params) => <Checkbox disabled checked={params.row.isDefault}/>
            },
            {
                field: 'actions', type: 'actions', width: 350,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button color="primary" onClick={() => handleEditTemplate(params.row.id)} >
                                <Localized id="edit">
                                    <span>_Edytuj</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];


    switch (sysMailFootersState.type) {
        case "empty":
        case "loading":
            return <LoadingContainer/>
        case "error":
            return <ErrorContainer error={sysMailFootersState.error}/>;
        case "loaded":
            const tableContent = (
                <>
                    <TableContainer component={Paper}>
                        <DataGrid style={{ minHeight: 100, height: 'calc(100vh - 275px)' }} localeText={getCustomLocaleText()} rows={rows} columns={columns} onRowClick={({ row }) => setSelectedRow(row)} slots={{ toolbar: QuickSearchToolbar }} />
                        <If condition={isSuper}>
                            <Box sx={{
                                display: 'flex',
                            }}>
                                <Fab sx={{
                                    margin: 1,
                                }}
                                onClick={() => handleNewTemplate()} color="secondary" size="small" >
                                    <AddIcon />
                                </Fab>
                                <Button sx={{ ml: 2 }} onClick={() => handleGenerateCode()}>
                                    <Localized id="default-company-settings-generate-script">
                                        <span>_Wygeneruj skrypt</span>
                                    </Localized>
                                </Button>
                            </Box>
                        </If>
                    </TableContainer>
                </>
            );

            if (selectedRow) {
                return (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                {tableContent}
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ padding: 2, border: '1px solid #ccc', backgroundColor: '#ffffff', position: 'relative', borderRadius: 1 }}>
                                    <IconButton
                                        onClick={() => setSelectedRow(null)}
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Podgląd stopki</Typography>
                                    <Box sx={{ p: 2, border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>
                                        <div dangerouslySetInnerHTML={{ __html: selectedRow.footer }} />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Dialog open={dialog.type !== "none"} fullWidth maxWidth="lg" keepMounted={false}>
                            <DialogContentDispatcher state={dialog} setter={setDialog} />
                        </Dialog>
                    </>
                )
            } else {
                return (
                    <>
                        {tableContent}
                        <Dialog open={dialog.type !== "none"} fullWidth maxWidth="lg" keepMounted={false}>
                            <DialogContentDispatcher state={dialog} setter={setDialog} />
                        </Dialog>
                    </>
                )
            }
    }
}
